// adminportal/src/pages/SettingsPage.tsx
import React, { useState, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { SettingsFormData, AVAILABLE_THEMES } from '../types/settings';
import { Palette, Building2, Image, RotateCcw, Save, DollarSign, Shield } from 'lucide-react';
import Copyright from '../components/Copyright';
import CostMonitoring from '../components/CostMonitoring';
import { MfaService } from '../services/mfaService';

function resolveApiBase(): string {
  // 1) runtime config.json (set in main.tsx via setGlobalConfig)
  const fromWindow = (window as { __APP_CONFIG?: { apiUrl?: string } }).__APP_CONFIG?.apiUrl;

  // 2) vite env (what we declare in vite-env.d.ts and set in pipelines)
  const fromVite = import.meta.env.VITE_API_BASE as string | undefined;

  // 3) optional legacy key if you still have it in local .env during dev
  const legacy = (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE;

  return fromWindow || fromVite || legacy || '';
}

export default function SettingsPage() {
  const {
    settings,
    updateSettings,
    resetToDefaults,
    applyTheme,
    saveSettingsToApi,
    clearCacheAndReload,
  } = useSettings();
  const [formData, setFormData] = useState<SettingsFormData>({
    companyName: settings.company.name,
    primaryColor: settings.company.primaryColor,
    secondaryColor: settings.company.secondaryColor,
    accentColor: settings.company.accentColor,
    theme: settings.company.theme,
    title: settings.title,
    subtitle: settings.subtitle,
    systemEmail: settings.system.email,
    favicon: settings.company.favicon || '',
    enableInboundDownloads: settings.company.enableInboundDownloads,
    enableInboundNotifications: settings.company.enableInboundNotifications,
    enableOutboundNotifications: settings.company.enableOutboundNotifications,
  });

  // MFA state
  const [mfaSecret, setMfaSecret] = useState<string>('');
  const [mfaCode, setMfaCode] = useState<string>('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaMessage, setMfaMessage] = useState('');

  // Update form data when settings change (e.g., when settings load from localStorage/API)
  useEffect(() => {
    setFormData({
      companyName: settings.company.name,
      primaryColor: settings.company.primaryColor,
      secondaryColor: settings.company.secondaryColor,
      accentColor: settings.company.accentColor,
      theme: settings.company.theme,
      title: settings.title,
      subtitle: settings.subtitle,
      systemEmail: settings.system.email,
      favicon: settings.company.favicon || '',
      enableInboundDownloads: settings.company.enableInboundDownloads,
      enableInboundNotifications: settings.company.enableInboundNotifications,
      enableOutboundNotifications: settings.company.enableOutboundNotifications,
    });
  }, [
    settings.company.name,
    settings.company.primaryColor,
    settings.company.secondaryColor,
    settings.company.accentColor,
    settings.company.theme,
  ]);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  // const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'branding' | 'appearance' | 'advanced' | 'mfa' | 'costs'
  >('branding');

  // File size limits (API Gateway default is 10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  const MAX_FILE_SIZE_MB = 10; // 10MB for display

  const apiBase = resolveApiBase();

  const handleInputChange = (field: keyof SettingsFormData, value: string | boolean) => {
    setFormData((prev: SettingsFormData) => ({ ...prev, [field]: value }));

    // Apply theme immediately when theme is changed
    if (field === 'theme') {
      applyTheme(value as any);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setMessage(
        `❌ File too large! Maximum size is ${MAX_FILE_SIZE_MB}MB. Current file: ${(file.size / (1024 * 1024)).toFixed(1)}MB`
      );
      return;
    }

    // Show local preview immediately
    setLogoPreview(URL.createObjectURL(file));
    setMessage('');

    try {
      // For logo, we'll store it locally and save to API
      const reader = new FileReader();
      reader.onload = async () => {
        const logoUrl = reader.result as string;

        // Update local state immediately for UI
        updateSettings({
          company: {
            ...settings.company,
            logo: logoUrl,
          },
        });

        // Save to API directly with the new logo
        try {
          const configResponse = await fetch('/config.json', { cache: 'no-store' });
          if (configResponse.ok) {
            const config = await configResponse.json();
            if (config.apiUrl) {
              const apiSettings = {
                theme: settings.company.theme,
                primaryColor: settings.company.primaryColor,
                secondaryColor: settings.company.secondaryColor,
                accentColor: settings.company.accentColor,
                companyName: settings.company.name,
                logo: logoUrl, // Use the new logo directly
                favicon: settings.company.favicon,
              };

              const response = await fetch(`${config.apiUrl}/settings`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiSettings),
              });

              if (response.ok) {
                setMessage('✅ Logo updated successfully');
              } else {
                setMessage('⚠️ Logo updated locally but failed to save to server');
              }
            }
          }
        } catch (apiError) {
          console.error('❌ Failed to save logo to API:', apiError);
          setMessage('⚠️ Logo updated locally but failed to save to server');
        }
      };
      reader.readAsDataURL(file);
    } catch (err: unknown) {
      // Error updating logo
      setMessage(`❌ ${err instanceof Error ? err.message : 'Logo update failed'}`);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - favicons should be images
    if (!file.type.startsWith('image/')) {
      setMessage('❌ Please upload an image file (PNG, ICO, SVG, etc.)');
      return;
    }

    // Check file size (favicons are typically small, but allow up to 1MB)
    const MAX_FAVICON_SIZE = 1 * 1024 * 1024; // 1MB
    if (file.size > MAX_FAVICON_SIZE) {
      setMessage(
        `❌ Favicon file too large! Maximum size is 1MB. Current file: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
      );
      return;
    }

    // Show local preview immediately
    setFaviconPreview(URL.createObjectURL(file));
    setMessage('');

    try {
      // Convert favicon to data URL and save to API
      const reader = new FileReader();
      reader.onload = async () => {
        const faviconUrl = reader.result as string;

        // Update local state immediately for UI
        updateSettings({
          company: {
            ...settings.company,
            favicon: faviconUrl,
          },
        });

        // Update form data
        setFormData(prev => ({ ...prev, favicon: faviconUrl }));

        // Save to API directly with the new favicon
        try {
          const configResponse = await fetch('/config.json', { cache: 'no-store' });
          if (configResponse.ok) {
            const config = await configResponse.json();
            if (config.apiUrl) {
              const apiSettings = {
                theme: settings.company.theme,
                primaryColor: settings.company.primaryColor,
                secondaryColor: settings.company.secondaryColor,
                accentColor: settings.company.accentColor,
                companyName: settings.company.name,
                logo: settings.company.logo,
                favicon: faviconUrl, // Use the new favicon directly
              };

              const response = await fetch(`${config.apiUrl}/settings`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiSettings),
              });

              if (response.ok) {
                setMessage('✅ Favicon updated successfully');
              } else {
                setMessage('⚠️ Favicon updated locally but failed to save to server');
              }
            }
          }
        } catch (apiError) {
          console.error('❌ Failed to save favicon to API:', apiError);
          setMessage('⚠️ Favicon updated locally but failed to save to server');
        }
      };
      reader.readAsDataURL(file);
    } catch (err: unknown) {
      // Error updating favicon
      setMessage(`❌ ${err instanceof Error ? err.message : 'Favicon update failed'}`);
    }
  };

  const handleSave = () => {
    updateSettings({
      company: {
        ...settings.company,
        name: formData.companyName,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        accentColor: formData.accentColor,
        theme: formData.theme,
        favicon: formData.favicon,
      },
      system: {
        email: formData.systemEmail,
      },
    });
    setMessage('✅ Settings saved successfully');
  };

  const handleReset = () => {
    resetToDefaults();
    setFormData({
      companyName: settings.company.name,
      primaryColor: settings.company.primaryColor,
      secondaryColor: settings.company.secondaryColor,
      accentColor: settings.company.accentColor,
      theme: settings.company.theme,
      title: settings.title,
      subtitle: settings.subtitle,
      systemEmail: settings.system.email,
      favicon: settings.company.favicon || '',
      enableInboundDownloads: settings.company.enableInboundDownloads,
      enableInboundNotifications: settings.company.enableInboundNotifications,
      enableOutboundNotifications: settings.company.enableOutboundNotifications,
    });
    setMessage('✅ Settings reset to defaults');
  };

  // MFA functions
  const handleMfaSetup = async () => {
    setMfaLoading(true);
    setMfaMessage('');

    const accessToken = localStorage.getItem('cognito_access_token');
    if (!accessToken) {
      setMfaMessage('❌ You must be logged in to set up MFA.');
      setMfaLoading(false);
      return;
    }

    try {
      const result = await MfaService.associateSoftwareToken(accessToken);

      if (result.success && result.secretCode) {
        setMfaSecret(result.secretCode);
        setMfaMessage(
          '✅ MFA setup started. Please scan the QR code or enter the secret key in your authenticator app.'
        );
      } else {
        setMfaMessage(`❌ ${result.error || 'Failed to get MFA secret. Please try again.'}`);
      }
    } catch (err: unknown) {
      setMfaMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaVerification = async () => {
    if (!mfaCode || mfaCode.length !== 6) {
      setMfaMessage('❌ Please enter a valid 6-digit MFA code');
      return;
    }

    setMfaLoading(true);
    setMfaMessage('');

    const accessToken = localStorage.getItem('cognito_access_token');
    if (!accessToken) {
      setMfaMessage('❌ You must be logged in to verify MFA.');
      setMfaLoading(false);
      return;
    }

    try {
      const result = await MfaService.completeMfaSetup(accessToken, mfaCode);

      if (result.success) {
        setMfaMessage('✅ MFA setup completed successfully! Your account is now protected.');
        setMfaSecret('');
        setMfaCode('');
      } else {
        setMfaMessage(`❌ ${result.error || 'MFA verification failed. Please try again.'}`);
      }
    } catch (err: unknown) {
      setMfaMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setMfaLoading(false);
    }
  };

  const tabs = [
    { id: 'branding', label: 'Branding', icon: Building2 },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'advanced', label: 'Advanced', icon: Image },
    { id: 'mfa', label: 'MFA Security', icon: Shield },
    { id: 'costs', label: 'Cost Monitoring', icon: DollarSign },
  ];

  return (
    <div className='p-6 max-w-4xl mx-auto'>
      <div className='flex items-center justify-between mb-6'>
        <h1 className='text-3xl font-bold'>Portal Settings</h1>
        <div className='flex gap-2'>
          <button
            onClick={handleReset}
            className='px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2 transition-colors'
          >
            <RotateCcw className='w-4 h-4' />
            Reset
          </button>
          <button
            onClick={handleSave}
            className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors'
          >
            <Save className='w-4 h-4' />
            Save Changes
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className='flex border-b border-gray-200 mb-6'>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() =>
                setActiveTab(tab.id as 'branding' | 'appearance' | 'advanced' | 'mfa' | 'costs')
              }
              className={`px-4 py-2 flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className='w-4 h-4' />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`p-4 rounded-lg mb-6 ${
            message.startsWith('✅')
              ? 'bg-green-900/20 text-green-300 border border-green-700/50'
              : 'bg-red-900/20 text-red-300 border border-red-700/50'
          }`}
        >
          {message}
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <div className='space-y-6'>
          {/* Branding Section */}
          <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-secondary'>
            <h3 className='text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2'>
              <Building2 className='w-5 h-5' />
              Company Branding
            </h3>

            <div className='space-y-4'>
              <div>
                <label
                  htmlFor='company-name'
                  className='block text-sm font-medium text-slate-300 mb-2'
                >
                  Company Name
                </label>
                <input
                  id='company-name'
                  type='text'
                  value={formData.companyName}
                  onChange={e => handleInputChange('companyName', e.target.value)}
                  className='form-input'
                  disabled
                />
                <p className='text-xs text-slate-400 mt-1'>
                  Company name is managed by your administrator
                </p>
              </div>

              <div>
                <label
                  htmlFor='system-email'
                  className='block text-sm font-medium text-slate-300 mb-2'
                >
                  System Email
                </label>
                <input
                  id='system-email'
                  type='email'
                  value={formData.systemEmail}
                  onChange={e => handleInputChange('systemEmail', e.target.value)}
                  className='form-input'
                  placeholder='noreply@yourcompany.com'
                />
                <p className='text-xs text-slate-400 mt-1'>
                  Email address for system notifications (must be verified in SES)
                </p>
              </div>

              <div>
                <label className='flex items-center space-x-3'>
                  <input
                    type='checkbox'
                    checked={formData.enableInboundNotifications}
                    onChange={e =>
                      handleInputChange('enableInboundNotifications', e.target.checked)
                    }
                    className='w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500 focus:ring-2'
                  />
                  <div>
                    <span className='text-sm font-medium text-slate-300'>
                      Enable Inbound File Notifications
                    </span>
                    <p className='text-xs text-slate-400 mt-1'>
                      When enabled, users will receive email notifications when files are uploaded
                      to inbound folders. When disabled, no notifications will be sent.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className='flex items-center space-x-3'>
                  <input
                    type='checkbox'
                    checked={formData.enableInboundDownloads}
                    onChange={e => handleInputChange('enableInboundDownloads', e.target.checked)}
                    className='w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500 focus:ring-2'
                    disabled={!formData.enableInboundNotifications}
                  />
                  <div>
                    <span
                      className={`text-sm font-medium ${formData.enableInboundNotifications ? 'text-slate-300' : 'text-slate-500'}`}
                    >
                      Enable Download Links in Inbound File Notifications
                    </span>
                    <p className='text-xs text-slate-400 mt-1'>
                      When enabled, users will receive direct download links for inbound files. When
                      disabled, users will only be notified that files are available. Requires
                      notifications to be enabled above.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  Current Logo
                </label>
                <div className='px-3 py-2 border border-white/10 rounded-md text-slate-100 form-input'>
                  {settings.company.logo ? (
                    <img
                      src={settings.company.logo}
                      alt='Company Logo'
                      className='h-8 object-contain'
                    />
                  ) : (
                    'No logo uploaded'
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor='logo-upload'
                  className='block text-sm font-medium text-slate-300 mb-2'
                >
                  Upload New Logo
                </label>
                <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-tertiary'>
                  <input
                    id='logo-upload'
                    type='file'
                    accept='image/*'
                    onChange={handleLogoUpload}
                    disabled={false}
                    className='file-input file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700'
                  />
                  <p className='text-sm text-slate-400 mt-1'>
                    Upload a logo to replace the default Shield icon
                  </p>
                  <p className='text-xs text-slate-500 mt-1'>
                    Maximum file size: {MAX_FILE_SIZE_MB}MB
                  </p>
                </div>

                {logoPreview && (
                  <div>
                    <label className='block text-sm font-medium text-slate-200 mb-2'>
                      Logo Preview
                    </label>
                    <img
                      src={logoPreview}
                      alt='Logo Preview'
                      className='h-20 rounded border border-white/10'
                    />
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor='favicon-upload'
                  className='block text-sm font-medium text-slate-300 mb-2'
                >
                  Favicon
                </label>
                <div className='space-y-3'>
                  {/* File Upload */}
                  <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-tertiary'>
                    <input
                      id='favicon-upload'
                      type='file'
                      accept='image/*'
                      onChange={handleFaviconUpload}
                      className='file-input file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700'
                    />
                    <p className='text-sm text-slate-400 mt-2'>
                      Upload a favicon image (PNG, ICO, SVG, etc.)
                    </p>
                    <p className='text-xs text-slate-500 mt-1'>
                      Recommended size: 16x16 or 32x32 pixels. Maximum file size: 1MB
                    </p>
                  </div>

                  {/* Or URL Input */}
                  <div>
                    <label
                      htmlFor='favicon-url'
                      className='block text-xs font-medium text-slate-400 mb-1'
                    >
                      Or enter a URL:
                    </label>
                    <input
                      id='favicon-url'
                      type='url'
                      value={formData.favicon && !formData.favicon.startsWith('data:') ? formData.favicon : ''}
                      onChange={e => handleInputChange('favicon', e.target.value)}
                      className='form-input text-sm'
                      placeholder='https://example.com/favicon.ico'
                    />
                  </div>

                  {/* Preview */}
                  {(faviconPreview || formData.favicon) && (
                    <div>
                      <label className='block text-sm font-medium text-slate-200 mb-2'>
                        Favicon Preview
                      </label>
                      <div className='flex items-center gap-4'>
                        <img
                          src={faviconPreview || formData.favicon}
                          alt='Favicon Preview'
                          className='w-16 h-16 object-contain border border-white/10 rounded'
                          onError={(e) => {
                            // Hide preview if image fails to load
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div className='text-xs text-slate-400'>
                          <p>This will appear in browser tabs</p>
                          <p className='text-slate-500 mt-1'>
                            {faviconPreview || formData.favicon?.startsWith('data:')
                              ? 'Uploaded image'
                              : 'From URL'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appearance Tab */}
      {activeTab === 'appearance' && (
        <div className='space-y-6'>
          {/* Appearance Section */}
          <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-secondary'>
            <h3 className='text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2'>
              <Palette className='w-5 h-5' />
              Color Customization
            </h3>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div>
                <label
                  htmlFor='primary-color'
                  className='block text-sm font-medium text-slate-300 mb-2'
                >
                  Primary Color
                </label>
                <input
                  id='primary-color'
                  type='color'
                  value={formData.primaryColor}
                  onChange={e => handleInputChange('primaryColor', e.target.value)}
                  className='color-picker'
                  aria-label='Select primary color'
                />
              </div>

              <div>
                <label
                  htmlFor='secondary-color'
                  className='block text-sm font-medium text-slate-300 mb-2'
                >
                  Secondary Color
                </label>
                <input
                  id='secondary-color'
                  type='color'
                  value={formData.secondaryColor}
                  onChange={e => handleInputChange('secondaryColor', e.target.value)}
                  className='color-picker'
                  aria-label='Select secondary color'
                />
              </div>

              <div>
                <label
                  htmlFor='accent-color'
                  className='block text-sm font-medium text-slate-300 mb-2'
                >
                  Accent Color
                </label>
                <input
                  id='accent-color'
                  type='color'
                  value={formData.accentColor}
                  onChange={e => handleInputChange('accentColor', e.target.value)}
                  className='color-picker'
                  aria-label='Select accent color'
                />
              </div>
            </div>
          </div>

          {/* Theme Section */}
          <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-secondary'>
            <h3 className='text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2'>
              <Palette className='w-5 h-5' />
              Theme Selection
            </h3>

            <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
              {AVAILABLE_THEMES.map(theme => (
                <div
                  key={theme.name}
                  className={`p-4 border rounded-lg hover:bg-white/10 transition-colors cursor-pointer ${
                    formData.theme === theme.name
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-white/10 bg-tertiary'
                  }`}
                  onClick={() => handleInputChange('theme', theme.name)}
                >
                  <div className='text-sm font-medium text-slate-200'>{theme.displayName}</div>
                  <div className='text-xs text-slate-400'>{theme.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Advanced Tab */}
      {activeTab === 'advanced' && (
        <div className='space-y-6'>
          {/* Advanced Section */}
          <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-secondary'>
            <h3 className='text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2'>
              <RotateCcw className='w-5 h-5' />
              Advanced Options
            </h3>

            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  Reset to Defaults
                </label>
                <p className='text-sm text-slate-400 mb-3'>
                  This will reset all your custom colors and theme settings back to the system
                  defaults.
                </p>
                <button
                  onClick={resetToDefaults}
                  className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors'
                >
                  Reset All Settings
                </button>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  Clear Cache & Reload
                </label>
                <p className='text-sm text-slate-400 mb-3'>
                  This will clear the cached settings and reload the page to fetch fresh data from
                  the server.
                </p>
                <button
                  onClick={clearCacheAndReload}
                  className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors'
                >
                  Clear Cache & Reload
                </button>
              </div>
            </div>
          </div>

          <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-secondary'>
            <h3 className='text-lg font-semibold text-slate-200 mb-4'>API Configuration</h3>

            <div>
              <label
                htmlFor='api-base-url'
                className='block text-sm font-medium text-slate-300 mb-2'
              >
                API Base URL
              </label>
              <input
                id='api-base-url'
                type='text'
                value={apiBase || 'Not configured'}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-100'
                disabled
              />
              <p className='text-xs text-slate-400 mt-1'>
                This is loaded from config.json or environment variables
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MFA Security Tab */}
      {activeTab === 'mfa' && (
        <div className='space-y-6'>
          <div className='border border-white/10 p-6 rounded-lg shadow-sm bg-secondary'>
            <h3 className='text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2'>
              <Shield className='w-5 h-5' />
              Multi-Factor Authentication (MFA)
            </h3>

            <div className='space-y-4'>
              <p className='text-slate-300 text-sm'>
                Add an extra layer of security to your admin account with Multi-Factor
                Authentication (MFA). This requires a second verification step using an
                authenticator app.
              </p>

              {mfaMessage && (
                <div
                  className={`p-4 rounded-lg border ${
                    mfaMessage.includes('✅')
                      ? 'bg-green-500/20 border-green-500/30 text-green-300'
                      : 'bg-red-500/20 border-red-500/30 text-red-300'
                  }`}
                >
                  {mfaMessage}
                </div>
              )}

              {!mfaSecret ? (
                <div className='space-y-4'>
                  <div className='bg-blue-500/10 border border-blue-500/20 rounded-lg p-4'>
                    <h4 className='text-sm font-medium text-blue-200 mb-2'>Setup Instructions</h4>
                    <ol className='text-xs text-blue-300 space-y-1 list-decimal list-inside'>
                      <li>Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                      <li>Click "Start MFA Setup" to get your secret key</li>
                      <li>Enter the secret key manually in your authenticator app</li>
                      <li>Enter the 6-digit code from your app to complete setup</li>
                    </ol>
                  </div>

                  <button
                    onClick={handleMfaSetup}
                    disabled={mfaLoading}
                    className='w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2'
                  >
                    {mfaLoading ? 'Starting MFA Setup...' : 'Start MFA Setup'}
                  </button>
                </div>
              ) : (
                <div className='space-y-4'>
                  <div className='bg-slate-800 p-4 rounded-lg border border-slate-600'>
                    <h4 className='text-sm font-medium text-slate-200 mb-2'>Secret Key:</h4>
                    <p className='text-xs text-slate-300 font-mono break-all bg-slate-900 p-2 rounded'>
                      {mfaSecret}
                    </p>
                    <p className='text-xs text-slate-400 mt-2'>
                      Enter this secret key manually in your authenticator app.
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor='mfaCode'
                      className='block text-sm font-medium text-slate-300 mb-2'
                    >
                      MFA Code (6 digits)
                    </label>
                    <input
                      type='text'
                      id='mfaCode'
                      value={mfaCode}
                      onChange={e => setMfaCode(e.target.value)}
                      maxLength={6}
                      placeholder='Enter 6-digit code from your authenticator app'
                      className='w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:border-blue-500 focus:outline-none'
                    />
                  </div>

                  <div className='flex gap-2'>
                    <button
                      onClick={handleMfaVerification}
                      disabled={mfaLoading || !mfaCode || mfaCode.length !== 6}
                      className='flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2'
                    >
                      {mfaLoading ? 'Verifying...' : 'Complete MFA Setup'}
                    </button>

                    <button
                      onClick={() => {
                        setMfaSecret('');
                        setMfaCode('');
                        setMfaMessage('');
                      }}
                      className='px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors'
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cost Monitoring Tab */}
      {activeTab === 'costs' && (
        <div className='space-y-6'>
          <CostMonitoring />
        </div>
      )}

      {/* API Configuration Warning */}
      {!apiBase && (
        <div className='bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4'>
          <p className='text-yellow-200'>
            ⚠️ Warning: API base URL is not configured. Ensure{' '}
            <code className='bg-slate-700 px-1 rounded'>config.json</code> has
            <code className='bg-slate-700 px-1 rounded'> apiUrl</code> or set{' '}
            <code className='bg-slate-700 px-1 rounded'>VITE_API_BASE</code>.
          </p>
        </div>
      )}

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
